import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart3, Users, Mail, MessageSquare, Calendar, TrendingUp, Download } from 'lucide-react';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentSubmissions, setRecentSubmissions] = useState([]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await axios.get(`${API}/analytics`);
        setStats(response.data.stats);
        setRecentSubmissions(response.data.recent_submissions);
      } catch (error) {
        // Error fetching analytics
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [API]);

  if (loading) {
    return (
      <div>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-slate-600">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <Header />

      <div className="pt-32 pb-20 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>

          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-black text-slate-900 mb-2">Analytics Dashboard</h1>
              <p className="text-slate-600">Track your website performance and lead generation</p>
            </div>
            <button className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-bold transition-colors flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Report
            </button>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-black text-slate-900 mb-1">{stats?.contacts || 0}</div>
              <div className="text-sm text-slate-600">Contact Submissions</div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-black text-slate-900 mb-1">{stats?.consultations || 0}</div>
              <div className="text-sm text-slate-600">Consultation Bookings</div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-black text-slate-900 mb-1">{stats?.newsletter || 0}</div>
              <div className="text-sm text-slate-600">Newsletter Subscribers</div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-orange-600" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-black text-slate-900 mb-1">{stats?.chat_messages || 0}</div>
              <div className="text-sm text-slate-600">Chat Messages</div>
            </div>
          </div>

          {/* Lead Magnets Stats */}
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-8 mb-8 text-white">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Download className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Lead Magnets Performance</h2>
                <p className="text-teal-100">Downloads and conversions</p>
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <div className="text-4xl font-black mb-1">{stats?.lead_magnets || 0}</div>
                <div className="text-teal-100">Total Downloads</div>
              </div>
              <div>
                <div className="text-4xl font-black mb-1">{stats?.magnet_conversion || '0%'}</div>
                <div className="text-teal-100">Conversion Rate</div>
              </div>
              <div>
                <div className="text-4xl font-black mb-1">Social Media</div>
                <div className="text-teal-100">Most Popular</div>
              </div>
              <div>
                <div className="text-4xl font-black mb-1">24h</div>
                <div className="text-teal-100">Avg Response Time</div>
              </div>
            </div>
          </div>

          {/* Recent Submissions */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Recent Submissions</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Type</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Name</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Email</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {recentSubmissions.length > 0 ? (
                    recentSubmissions.map((submission) => (
                      <tr key={`${submission.type}-${submission.email}-${submission.date}`} className="hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">
                            {submission.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-900">{submission.name}</td>
                        <td className="px-6 py-4 text-slate-600">{submission.email}</td>
                        <td className="px-6 py-4 text-slate-600">{submission.date}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                        No submissions yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;